import React, { lazy, Suspense } from "react";
// Import core components directly
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EczemaDataProvider } from "./context/EczemaDataContext";
import { SidebarProvider } from "./context/SidebarContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ChatTransferProvider } from "./context/ChatTransferContext";
import { ChatPersistProvider } from "./context/ChatPersistContext";
import { InsightsChatPersistProvider } from "./context/InsightsChatPersistContext";
import { EncryptionProvider } from "./context/EncryptionContext";
import { AiConsentProvider } from "./context/AiConsentContext";
import EncryptionGate from "./components/encryption/EncryptionGate";
import LoadingSquiggle from "./components/LoadingSquiggle";
import AnalyticsPageView from "./components/AnalyticsPageView";

// Keep the loading squiggle on screen long enough to play at least one full
// animation cycle. Without this, a fast chunk load swaps the fallback out
// mid-frame and the squiggle just flashes a frozen frame before the page shows.
const lazyWithMinDelay = <T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  minMs = 1000
) =>
  lazy(() =>
    Promise.all([
      factory(),
      new Promise((resolve) => setTimeout(resolve, minMs)),
    ]).then(([mod]) => mod as { default: T })
  );

// Lazy load route components
const Track = lazyWithMinDelay(() => import("./pages/Track"));
const Insights = lazy(() => import("./pages/Insights"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const IOSUserProfile = lazy(() => import("./components/IOSUserProfile"));
const Add = lazy(() => import("./pages/Add"));
const Chat = lazy(() => import("./pages/Chat"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (was cacheTime in v4)
      retry: 1,
    },
  },
});

const withSuspense = (node: React.ReactNode) => (
  <Suspense fallback={<LoadingSquiggle />}>{node}</Suspense>
);

const withEczemaData = (node: React.ReactNode) => (
  <EczemaDataProvider>{withSuspense(node)}</EczemaDataProvider>
);

// Capacitor evaluates synchronously at module load. Native users skip the
// home redirect entirely — no lazy chunk, no useEffect, no white flash.
const IS_NATIVE = Capacitor.isNativePlatform();

// Landing page is WIP — send web visitors to /login until it's ready.
const HomeRoute: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const fromLogout = (location.state as { fromLogout?: boolean } | null)?.fromLogout;

  if (IS_NATIVE) {
    return <Navigate to="/chat" replace />;
  }

  if (loading) {
    return <LoadingSquiggle />;
  }

  if (user && !fromLogout) {
    return <Navigate to="/chat" replace />;
  }

  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <EncryptionProvider>
              <AiConsentProvider>
              <ChatTransferProvider>
              <ChatPersistProvider>
              <InsightsChatPersistProvider>
                <SidebarProvider>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <BrowserRouter future={{ v7_startTransition: true }}>
                      <AnalyticsPageView />
                      <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<HomeRoute />} />
                        <Route path="/login" element={
                          withSuspense(<Login />)
                        } />
                        <Route path="/register" element={
                          withSuspense(<Register />)
                        } />
                        <Route path="/terms" element={
                          withSuspense(<Terms />)
                        } />
                        <Route path="/privacy" element={
                          withSuspense(<Privacy />)
                        } />

                        {/* Protected routes — wrapped in app Layout */}
                        <Route element={<ProtectedRoute />}>
                          <Route element={<EncryptionGate />}>
                          <Route element={<Layout />}>
                            <Route path="/logs" element={withEczemaData(<Track />)} />
                            <Route path="/track" element={<Navigate to="/logs" replace />} />
                            <Route path="/chat" element={withEczemaData(<Chat />)} />
                            <Route path="/insights" element={withEczemaData(<Insights />)} />
                            <Route path="/settings" element={
                              withSuspense(<Settings />)
                            } />
                            <Route path="/profile" element={withEczemaData(<IOSUserProfile />)} />
                            <Route path="/add" element={
                              withSuspense(<Add />)
                            } />
                            <Route path="*" element={
                              withSuspense(<NotFound />)
                            } />
                          </Route>
                          </Route>
                        </Route>
                      </Routes>
                    </BrowserRouter>
                  </TooltipProvider>
                </SidebarProvider>
              </InsightsChatPersistProvider>
              </ChatPersistProvider>
              </ChatTransferProvider>
              </AiConsentProvider>
            </EncryptionProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
};

export default App;
